import styled, { useTheme } from 'styled-components';
import React, { ReactNode, useCallback, useState } from 'react';
import { Order } from '../../../types/order/Order';
import DateTime from '../../atoms/DateTime/DateTime';
import Status from '../../atoms/Status/Status';
import OrderExpiresIn from './OrderExpiresIn';
import OrderStatus from '../../../types/order/OrderStatus';
import StatusTypes from '../../../types/StatusTypes';
import { ConnectedStore } from '../../../types/ConnectedStore';
import ProductCode from '../../../types/ProductCode';
import Column from '../../atoms/Header/Column';
import CompactStoreLabel from '../../atoms/Store/CompactStoreLabel';
import MultiactionButton from '../../atoms/Button/MultiactionButton';
import ArrowDownSLineIcon from 'remixicon-react/ArrowDownSLineIcon';

export interface OrderHeaderAction {
    key: string;
    label: ReactNode;
    action: () => void;
    show: boolean;
    dataTestId?: string;
}

type Props = {
    icon?: string;
    order: Order;
    store: ConnectedStore | undefined;
    actions?: OrderHeaderAction[];
};

const OrderDetailsHeader: React.FC<Props> = ({ icon, order, store, actions }: Props) => {
    const theme = useTheme();
    const [copiedPos, setCopiedPos] = useState<{ x: number; y: number } | null>(null);

    const copyOrderNumber = useCallback(
        (e: React.MouseEvent) => {
            if (!order.orderNumber) return;
            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
            setCopiedPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
            navigator.clipboard.writeText(order.orderNumber).then(() => {
                setTimeout(() => setCopiedPos(null), 1750);
            });
        },
        [order.orderNumber]
    );

    const renderStoreId = () => {
        if (!store) return <BoldText>{order.storeId}</BoldText>;

        return <CompactStoreLabel store={store} />;
    };

    const showExpiryDate =
        order.status === OrderStatus.NotActivated ||
        order.status === OrderStatus.PartActivated ||
        order.status === OrderStatus.OnHold;

    const hasActions = actions && actions.some(a => a.show);

    return (
        <HeaderContentWrapper>
            <Row>
                <Column>
                    <Label>Reference</Label>
                    <OrderNameContent>
                        {order.orderNumber ? (
                            <CopyableText onClick={copyOrderNumber} title="Click to copy">
                                <TruncatedBoldText title={order.orderNumber}>
                                    {order.orderNumber}
                                </TruncatedBoldText>
                                {copiedPos && (
                                    <CopiedIndicator
                                        style={{ left: copiedPos.x, top: copiedPos.y }}
                                    >
                                        Copied!
                                    </CopiedIndicator>
                                )}
                            </CopyableText>
                        ) : (
                            <BoldText>
                                <NotSet>Not Set</NotSet>
                            </BoldText>
                        )}
                        <Status type={StatusTypes.Order} status={order.status} />
                    </OrderNameContent>
                </Column>
                {icon && <Column>{icon}</Column>}
                <Column>
                    <Label>Created</Label>
                    <Content>
                        <DateTime>{order.placedAt}</DateTime>
                    </Content>
                </Column>
                {order.expiresAt && showExpiryDate && (
                    <Column>
                        <Label>Expires</Label>
                        <Content>
                            <OrderExpiresIn orderExpire={order.expiresAt} />
                            <DateTime>{order.expiresAt}</DateTime>
                        </Content>
                    </Column>
                )}
                <Column>
                    <Label>Currency</Label>
                    <Content>
                        <BoldText>{order.currency}</BoldText>
                    </Content>
                </Column>

                <Column>
                    <Label>Store</Label>
                    <Content>{renderStoreId()}</Content>
                </Column>

                {order.productCode === ProductCode.SERVICE_INVOICE && (
                    <Column>
                        <Label>Liability</Label>
                        <Content>🟡 Merchant liable for credit</Content>
                    </Column>
                )}

                {order.productCode === ProductCode.NO_FRAUD_INVOICE && (
                    <Column>
                        <Label>Liability</Label>
                        <Content>🟡 Merchant liable if contested</Content>
                    </Column>
                )}
            </Row>
            {actions && actions.length > 0 && (
                <HeaderRight>
                    <MultiactionButton
                        actions={actions}
                        data-testid="order-actions-dropdown"
                        disabled={!hasActions}
                    >
                        <>
                            Actions <ArrowDownSLineIcon size={theme.icon.size.small} />
                        </>
                    </MultiactionButton>
                </HeaderRight>
            )}
        </HeaderContentWrapper>
    );
};

const NotSet = styled.span`
    color: ${props => props.theme.colors.text.secondary};
`;

const BoldText = styled.span`
    font-family: ${props => props.theme.text.font.medium};
    margin-right: 2rem;
`;

const TruncatedBoldText = styled(BoldText)`
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    display: inline-block;
    max-width: 30ch;
    vertical-align: middle;
`;

const CopyableText = styled.span`
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    position: relative;

    &:hover {
        opacity: 0.7;
    }
`;

const CopiedIndicator = styled.span`
    font-size: ${props => props.theme.text.size.small};
    color: ${props => props.theme.colors.text.primary};
    background: ${props => props.theme.colors.light};
    border: 0.1rem solid ${props => props.theme.colors.border};
    border-radius: 0.4rem;
    padding: 0.2rem 0.6rem;
    position: absolute;
    z-index: 10;
    white-space: nowrap;
    pointer-events: none;
    transform: translate(1.33rem, 1.33rem);
    box-shadow: 0 0.2rem 0.4rem rgba(0, 0, 0, 0.1);
`;

const Row = styled.div`
    display: flex;
    justify-content: flex-start;
    flex-direction: row;
    flex-wrap: wrap;
    font-size: ${props => props.theme.text.size.medium};
`;

const HeaderContentWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
`;

const HeaderRight = styled.div`
    display: flex;
    align-items: end;
    padding: 0 0 0.4rem 1.5rem;
    border-left: 0.2rem solid ${props => props.theme.colors.text.subtle};
`;

const OrderNameContent = styled.div`
    margin-top: -0.4rem;
`;

const Label = styled.div`
    font-size: ${props => props.theme.text.size.small};
    margin-bottom: 0.3rem;
    color: ${props => props.theme.colors.text.secondary};
`;

const Content = styled.div`
    color: ${props => props.theme.colors.text.primary};
`;

export default OrderDetailsHeader;
